/* Document reader — engine entry point.
 *
 * One job: turn a file into a LISTENING EDITION — typed blocks, an outline, and
 * a plan the listener controls — instead of a flat character stream.
 *
 * Why this exists, measured before it did (RU shard, 90 days; four real arXiv
 * papers through the old pipeline):
 *   1108 sessions pressed play · 39 reached the end · 21% average progress
 *   the bibliography was 11–36% of everything spoken, read as single initials
 *   526 words per four papers were broken by line-end hyphenation
 *   the vertical arXiv stamp was voiced in the middle of a sentence
 *
 * Loaded with @moduleGraph, so relative imports below carry the version in the
 * PATH and cannot be served stale (see CLAUDE.md, "Вход ES-модуля").
 */

import { linesFromPage } from './pdf-layout.js';
import { typeLines } from './blocks.js';
import { blocksFromHtml } from './docx.js';
import { repair } from './text.js';
import { prepare, apply, fullCost, DEFAULT_PLAN, KINDS } from './plan.js';
import { CAPTION_RE } from './blocks.js';

/* Plain text (TXT, paste, OCR hand-off) has no geometry and no markup. It still
 * has paragraphs, and a heading is still a short line with no full stop, so the
 * same typing pass runs — just with every geometric test returning undefined. */
function linesFromText(text) {
    return String(text).split(/\n/).map(function (t) {
        return { text: t, page: 1, y: null, x: null, right: null, col: 0,
            size: 0, bold: false, math: false, mono: false, gaps: 0,
            pageH: 0, pageW: 0, rotated: false };
    }).filter(function (l) { return l.text.trim(); });
}

/**
 * @param {object} pdf        pdf.js PDFDocumentProxy
 * @param {function} onPage   (done, total) — progress for a 500-page file
 */
async function fromPdf(pdf, onPage) {
    var all = [];
    var total = pdf.numPages;
    for (var i = 1; i <= total; i++) {
        var page = await pdf.getPage(i);
        var viewport = page.getViewport({ scale: 1 });
        var content = await page.getTextContent();
        var lines = linesFromPage(content, viewport, i);
        for (var j = 0; j < lines.length; j++) all.push(lines[j]);
        if (onPage) onPage(i, total);
        // Yield so a 500-page document does not freeze the tab. The old
        // extractor recursed through pages with .then, which yielded by
        // accident; an await loop must do it on purpose.
        if (i % 8 === 0) await new Promise(function (r) { setTimeout(r, 0); });
    }
    return finish(all, { pages: total, source: 'pdf' });
}

function fromDocxHtml(html) {
    var blocks = blocksFromHtml(html);
    // Re-run the shared typing pass so captions, references and tables are
    // recognised the same way in both formats — one set of rules, not two.
    var lines = [];
    blocks.forEach(function (b) {
        String(b.text).split(/\n/).forEach(function (t) {
            if (!t.trim()) return;
            lines.push({ text: t, page: 0, y: null, x: null, right: null, col: 0,
                size: b.kind === 'heading' ? 20 - (b.level || 1) : 10,
                bold: b.kind === 'heading', math: false,
                mono: b.kind === 'code', gaps: b.kind === 'table' ? 3 : 0,
                pageH: 0, pageW: 0, rotated: false, declared: b.kind, level: b.level });
        });
    });
    return finish(lines, { pages: 0, source: 'docx' });
}

function fromText(text) {
    return finish(linesFromText(text), { pages: 0, source: 'text' });
}

function finish(lines, meta) {
    // Repair the words BEFORE anything is typed or split: a hyphen at a line
    // end also confuses the sentence splitter, and a stamp glued into a
    // sentence confuses the paragraph merge.
    var joined = 0, stamps = 0;
    lines.forEach(function (l) { l._raw = l.text; });
    var glued = lines.map(function (l) { return l.text; }).join('\n');
    var rep = repair(glued);
    joined = rep.joined; stamps = rep.stamps;
    var fixed = rep.text.split('\n');
    // repair() preserves the line count (it re-emits the newline it consumed),
    // but a defensive check costs nothing and a silent off-by-one here would
    // shift every geometry field onto the wrong text.
    if (fixed.length === lines.length) {
        for (var i = 0; i < lines.length; i++) lines[i].text = fixed[i];
    }

    var typed = typeLines(lines, { pages: meta.pages });
    var prepared = prepare(typed.blocks, {});
    var full = fullCost(prepared);

    return {
        source: meta.source,
        pages: meta.pages,
        blocks: typed.blocks,
        stats: Object.assign({}, typed.stats, { hyphensJoined: joined, stampsRemoved: stamps }),
        outline: prepared.outline,
        prepared: prepared,
        full: full,
        /** Apply a plan. Cheap — safe to call on every switch. */
        read: function (plan, words, opts) { return apply(prepared, plan, words, opts); }
    };
}

var Engine = {
    fromPdf: fromPdf,
    fromDocxHtml: fromDocxHtml,
    fromText: fromText,
    DEFAULT_PLAN: DEFAULT_PLAN,
    KINDS: KINDS,
    CAPTION_RE: CAPTION_RE
};

if (typeof window !== 'undefined') {
    window.DocReaderEngine = Engine;
    try { window.dispatchEvent(new Event('docreader:engine-ready')); } catch (_) {}
}

export default Engine;
export { fromPdf, fromDocxHtml, fromText };
